import { CheckCircle2, Circle, BookOpen, Layers, ChevronRight, Settings } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { PageView, Module } from '@/types'
import type { useProgress } from '@/hooks/useProgress'

interface SidebarProps {
  modules: Module[]
  currentView: PageView
  onNavigate: (view: PageView) => void
  progress: ReturnType<typeof useProgress>
  hasContentOverrides: boolean
}

export function Sidebar({
  modules,
  currentView,
  onNavigate,
  progress,
  hasContentOverrides,
}: SidebarProps) {
  const isModuleActive = (id: string) =>
    currentView.page === 'module' && currentView.moduleId === id

  return (
    <aside
      className="flex-shrink-0 flex flex-col bg-[var(--bg-primary)] border-r border-[var(--border)] no-print"
      style={{ width: 'var(--sidebar-width)' }}
    >
      {/* Logo */}
      <div
        className="flex items-center gap-3 px-5 py-4 border-b border-[var(--border)] cursor-pointer"
        onClick={() => onNavigate({ page: 'dashboard' })}
      >
        <img
          src="/images/365hound-icon.png"
          alt="365 Hound"
          className="w-8 h-8 flex-shrink-0 object-contain"
        />
        <div className="min-w-0">
          <div className="font-semibold text-sm text-[var(--text-primary)] truncate">365 Hound</div>
          <div className="text-xs text-[var(--text-muted)] truncate">Partner Training</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-5 py-3 bg-[var(--bg-secondary)] border-b border-[var(--border)]">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-medium text-[var(--text-secondary)]">Your progress</span>
          <span className="text-xs font-semibold text-[var(--accent-blue)]">
            {progress.completedCount}/{progress.totalCount}
          </span>
        </div>
        <div className="w-full h-1.5 bg-[var(--bg-tertiary)] rounded-full overflow-hidden">
          <div
            className="h-full bg-[var(--accent-blue)] rounded-full transition-all duration-500"
            style={{ width: `${progress.progressPercent}%` }}
          />
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3">
        <NavItem
          label="Overview"
          icon={<Layers size={16} />}
          active={currentView.page === 'dashboard'}
          onClick={() => onNavigate({ page: 'dashboard' })}
        />

        <div className="px-5 py-2">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-[var(--text-muted)]">
            Selling Methods
          </span>
        </div>

        {modules.map(mod => (
          <button
            key={mod.id}
            onClick={() => onNavigate({ page: 'module', moduleId: mod.id })}
            className={cn(
              'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors duration-100',
              isModuleActive(mod.id)
                ? 'bg-[var(--accent-blue-light)] text-[var(--accent-blue)]'
                : 'text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]',
            )}
          >
            <span className="flex-shrink-0">
              {progress.isComplete(mod.id) ? (
                <CheckCircle2 size={16} className="text-emerald-500" />
              ) : (
                <Circle
                  size={16}
                  className={isModuleActive(mod.id) ? 'text-[var(--accent-blue)]' : 'text-[var(--border-strong)]'}
                />
              )}
            </span>
            <span className="flex-1 min-w-0">
              <span className="text-xs text-[var(--text-muted)] block">Method {mod.number}</span>
              <span className="text-sm font-medium truncate block leading-tight">{mod.title}</span>
            </span>
            {isModuleActive(mod.id) && (
              <ChevronRight size={14} className="flex-shrink-0 text-[var(--accent-blue)]" />
            )}
          </button>
        ))}

        <div className="px-5 pt-3 pb-1">
          <div className="h-px bg-[var(--border)]" />
        </div>

        <NavItem
          label="Quick Reference"
          icon={<BookOpen size={16} />}
          active={currentView.page === 'quick-reference'}
          onClick={() => onNavigate({ page: 'quick-reference' })}
        />
      </nav>

      {/* Admin link at bottom */}
      <div className="border-t border-[var(--border)]">
        <button
          onClick={() => onNavigate({ page: 'admin' })}
          className={cn(
            'w-full flex items-center gap-2.5 px-4 py-3 text-sm transition-colors',
            currentView.page === 'admin'
              ? 'bg-[var(--accent-blue-light)] text-[var(--accent-blue)]'
              : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]',
          )}
        >
          <Settings size={14} />
          <span>Admin</span>
          {hasContentOverrides && (
            <span className="ml-auto w-2 h-2 rounded-full bg-[var(--accent-orange)]" />
          )}
        </button>
        <p className="px-4 pb-3 text-[10px] text-[var(--text-muted)] leading-relaxed">
          Train-the-Trainer 1 April 2026
        </p>
      </div>
    </aside>
  )
}

function NavItem({
  label,
  icon,
  active,
  onClick,
}: {
  label: string
  icon: React.ReactNode
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors duration-100',
        active
          ? 'bg-[var(--accent-blue-light)] text-[var(--accent-blue)]'
          : 'text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]',
      )}
    >
      {icon}
      {label}
    </button>
  )
}
